import React, { Suspense, lazy, useCallback, useState } from 'react';
import type { ProfessionalReviewTargetI } from '@/containers/Reviews/types';

// Solo lo abre un profesional del equipo desde su aviso: se carga recién al tocarlo.
const ProfessionalReviewDialog = lazy(
  () => import('@/containers/Reviews/components/ProfessionalReviewDialog')
);

export interface ProfessionalReviewStateI {
  target: ProfessionalReviewTargetI;
  open: boolean;
}

/**
 * La reseña que abrió el aviso `review_for_professional`. Al cerrar se conserva la última, para que
 * el diálogo salga con su contenido en vez de vaciarse.
 */
export const useProfessionalReviewDialog = () => {
  const [state, setState] = useState<ProfessionalReviewStateI | null>(null);
  const openReview = useCallback((target: ProfessionalReviewTargetI) => setState({ target, open: true }), []);
  const closeReview = useCallback(() => setState((prev) => (prev ? { ...prev, open: false } : prev)), []);
  return { state, openReview, closeReview };
};

/**
 * El diálogo de la reseña, en la campanita y en Notificaciones. `hidden`: el turno está abierto
 * encima; la reseña se oculta sin cerrarse y vuelve al cerrar el turno (como en Reseñas).
 */
export const ProfessionalReviewHost: React.FC<{
  state: ProfessionalReviewStateI | null;
  hidden?: boolean;
  onClose: () => void;
  onOpenBooking?: (bookingId: number) => void;
}> = ({ state, hidden = false, onClose, onOpenBooking }) =>
  state ? (
    <Suspense fallback={null}>
      <ProfessionalReviewDialog
        target={state.target}
        open={state.open && !hidden}
        onClose={onClose}
        onOpenBooking={onOpenBooking}
      />
    </Suspense>
  ) : null;
